import React, {CSSProperties, useContext} from "react";
import ComplianceManagerContextProvider, {CMContext} from "../context-provider";
import {Alert, Button, Col, Drawer, Icon, Layout, message, Row, Spin, Tooltip} from "antd";
import ComplianceManager, {ComplianceManagerProps} from "../compliance-manager";
import {DrawerProps} from "antd/lib/drawer";
import useBreakpoints from "../../../../hooks/useBreakpoints";
import {usePaxNodeQuery, usePersonQuery} from "../../graphql";
import {ComplianceCheckInPackage} from "../../../../__generated__/graphql/types";

export type ComplianceManagerDrawerProps = DrawerProps & ((
    {
        paxNodeId?: string
    }
) | (
    {
        checkInPackage: ComplianceCheckInPackage
    }
));

/**
 * Wraps the ComplianceManager component in a drawer which can be controlled by a React Context
 * @constructor
 */
export const ComplianceManagerDrawer: React.FC<ComplianceManagerDrawerProps> = (props) => {

    const { visible, ...restProps } = props;

    let paxNodeId: string | undefined = undefined;
    let checkInPackage: ComplianceCheckInPackage | undefined = undefined;

    if ('paxNodeId' in props){
        paxNodeId = props.paxNodeId;
    }

    if ('checkInPackage' in props){
        checkInPackage = props.checkInPackage;
    }

    const bp = useBreakpoints();

    let args = {
        paxNodeId: paxNodeId,
        checkInPackage: checkInPackage,
        drawerOpen: visible
    }

    const ctx = useContext(CMContext);

    if (ctx){
        args = ctx as any;
    }

    const paxNodeData = usePaxNodeQuery(args.paxNodeId, false, 5000);
    const personData = usePersonQuery(args.checkInPackage?.fsPersonID, false, 5000);

    const bodyStyle: CSSProperties = {
        padding: 0,
        ...restProps?.bodyStyle,
    }

    let title: React.ReactNode = <Row type="flex" gutter={12}>
        <Col>
            <Spin indicator={<Icon type="loading" />} />
        </Col>
        <Col>
            Loading Compliance Details
        </Col>
    </Row>

    let id = '';
    let lastName = '';
    let firstName = '';
    let tooltipTitle = '';

    if (personData?.person){
        lastName = personData.person.lastName;
        firstName = personData.person.firstName;
        id = personData.person._id;
        tooltipTitle = 'Copy Person ID';
    }
    if (paxNodeData?.paxNode){
        lastName = paxNodeData.paxNode.lastName;
        firstName = paxNodeData.paxNode.firstName;
        id = paxNodeData.paxNode._id;
        tooltipTitle = 'Copy PAX ID';
    }

    if (paxNodeData.paxNode || personData.person){
        title = 'Compliance Details for ' + firstName + ' ' + lastName;
        title = <Row type="flex">
            <Col style={{ flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {title}
            </Col>
            <Col>
                <Tooltip title={tooltipTitle}>
                    <Button
                        style={{
                            marginLeft: 12
                        }}
                        icon="copy"
                        size="small"
                        onClick={() => {
                            navigator.clipboard.writeText(id)
                                .then(() => message.success(`'${id}' copied to clipboard`));
                        }}
                    />
                </Tooltip>
            </Col>
        </Row>
    }

    let errorAlert: React.ReactNode = null;

    if (paxNodeData.error){
        errorAlert = <Alert
            type="error"
            showIcon
            message="Failed to load passenger"
            style={{ margin: 24 }}
        />
    }

    let complianceManager: React.ReactNode = null;

    if ('paxNodeId' in args && args.paxNodeId){
        complianceManager = <ComplianceManager
            paxNodeId={args.paxNodeId}
            defaultTab={ctx?.defaultProviderTab}
        />
    }
    else if ('checkInPackage' in args && args.checkInPackage){
        complianceManager = <ComplianceManager
            checkInPackage={args.checkInPackage}
            defaultTab={ctx?.defaultProviderTab}
        />
    }
    else {
        complianceManager = <Alert
            type="warning"
            showIcon
            message="No passenger specified"
            style={{ margin: 24 }}
        />
    }

    return (
        <Drawer
            title={title}
            width={bp.min('medium') ? '50rem' : '100%'}
            {...restProps}
            bodyStyle={bodyStyle}
            visible={args.drawerOpen}
            onClose={(e) => {
                if (typeof ctx.close === 'function'){
                    ctx.close();
                }
                else
                {
                    props?.onClose(e);
                }
            }}
            className="ant-drawer-flex"
        >
            {errorAlert}
            <Layout>
                {complianceManager}
            </Layout>
        </Drawer>
    )
}

/**
 * Wraps a component giving it the ability to display the Compliance Manager drawer.
 * Also establishes the React context allowing buttons nested deep in the component tree to open this drawer.
 * @param Component
 */
export function withComplianceManagerDrawer<P extends object>(Component: React.ComponentType<P>){
    return function WithComplianceManagerDrawer(props: P){
        return <ComplianceManagerContextProvider>
            <Component {...props} />
            <ComplianceManagerDrawer />
        </ComplianceManagerContextProvider>
    }
}